import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
// El SDK no importa la librería de toasts: la super-app (Expo) no puede
// montar un componente web. Ver `api/notifier.ts` (YGO-261).
import { notify } from '../api/notifier';

// =============================================================================
// TYPES
// =============================================================================

export type OrderSocketEvent = {
  orderId: number;
  buyerId: number;
  sellerId: number;
  status:
    | 'PENDING'
    | 'CONFIRMED'
    | 'PAID'
    | 'SHIPPED'
    | 'DELIVERED'
    | 'COMPLETED'
    | 'CANCELLED';
  eventType:
    | 'CREATED'
    | 'CONFIRMED'
    | 'REJECTED'
    | 'PAYMENT_CONFIRMED'
    | 'SHIPPED'
    | 'DELIVERED'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'MESSAGE'
    | 'REVIEW'
    | 'BUYER_REVIEW'
    | 'UPDATED';
  message?: string;
  userName: string;
  userId: number;
};

type ServerToClientEvents = {
  test: () => void;
  'order:created': (payload: OrderSocketEvent) => void;
  'order:confirmed': (payload: OrderSocketEvent) => void;
  'order:rejected': (payload: OrderSocketEvent) => void;
  'order:paid': (payload: OrderSocketEvent) => void;
  'order:shipped': (payload: OrderSocketEvent) => void;
  'order:delivered': (payload: OrderSocketEvent) => void;
  'order:cancelled': (payload: OrderSocketEvent) => void;
  'order:message': (payload: OrderSocketEvent) => void;
  'order:review': (payload: OrderSocketEvent) => void;
  'order:buyer-review': (payload: OrderSocketEvent) => void;
  'order:updated': (payload: OrderSocketEvent) => void;
  // Ver `session:changed` en `api/socket-types.ts`.
  'session:changed': () => void;
};

type ClientToServerEvents = {
  test: () => void;
  'order:join': (orderId: number) => void;
  'order:leave': (orderId: number) => void;
};

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// =============================================================================
// CONTEXT
// =============================================================================

interface OrderSocketContextType {
  isConnected: boolean;
  joinOrder: (orderId: number) => void;
  leaveOrder: (orderId: number) => void;
}

const OrderSocketContext = createContext<OrderSocketContextType | undefined>(
  undefined
);

// =============================================================================
// TOAST MESSAGES
// =============================================================================

const EVENT_MESSAGES: Record<
  OrderSocketEvent['eventType'],
  { title: string; description: (userName: string) => string }
> = {
  CREATED: {
    title: 'Nuevo pedido',
    description: (userName) => `${userName} realizo un pedido`,
  },
  CONFIRMED: {
    title: 'Pedido confirmado',
    description: (userName) => `${userName} confirmo tu pedido`,
  },
  REJECTED: {
    title: 'Pedido rechazado',
    description: (userName) => `${userName} rechazo tu pedido`,
  },
  PAYMENT_CONFIRMED: {
    title: 'Pago confirmado',
    description: (userName) => `${userName} confirmo el pago`,
  },
  SHIPPED: {
    title: 'Pedido enviado',
    description: (userName) => `${userName} envio tu pedido`,
  },
  DELIVERED: {
    title: 'Pedido entregado',
    description: (userName) => `${userName} confirmo la entrega`,
  },
  COMPLETED: {
    title: 'Pedido completado',
    description: (userName) => `${userName} completo la transaccion`,
  },
  CANCELLED: {
    title: 'Pedido cancelado',
    description: (userName) => `${userName} cancelo el pedido`,
  },
  MESSAGE: {
    title: 'Nuevo mensaje',
    description: (userName) => `${userName} envio un mensaje`,
  },
  REVIEW: {
    title: 'Nueva calificacion',
    description: (userName) => `${userName} dejo una calificacion`,
  },
  BUYER_REVIEW: {
    title: 'Te calificaron',
    description: (userName) => `${userName} te califico como comprador`,
  },
  UPDATED: {
    title: 'Pedido actualizado',
    description: (userName) => `${userName} actualizo el pedido`,
  },
};

// =============================================================================
// PROVIDER PROPS
// =============================================================================

export interface OrderSocketProviderProps {
  children: ReactNode;
  /** WebSocket server URL */
  wsUrl: string;
  /** Current user ID (if authenticated) */
  userId?: number | null;
  /** App name for tracking */
  appName: string;
  /** App version for tracking */
  appVersion: string;
  /**
   * El API avisó `session:changed` (cambió el staff del usuario): la app
   * vuelve a pedir la sesión. Se guarda en un ref: si entrara en las
   * dependencias del efecto, cada callback nuevo reconectaría el socket.
   */
  onSessionChanged?: () => void;
}

// =============================================================================
// PROVIDER
// =============================================================================

export function OrderSocketProvider({
  children,
  wsUrl,
  userId,
  appName,
  appVersion,
  onSessionChanged,
}: OrderSocketProviderProps) {
  const queryClient = useQueryClient();
  const socketRef = useRef<TypedSocket | null>(null);
  const onSessionChangedRef = useRef(onSessionChanged);
  useEffect(() => {
    onSessionChangedRef.current = onSessionChanged;
  }, [onSessionChanged]);
  const [isConnected, setIsConnected] = useState(false);

  // Connect socket when authenticated
  useEffect(() => {
    // Don't connect if not authenticated or no WS URL
    if (!userId || !wsUrl) {
      return;
    }

    const newSocket: TypedSocket = io(wsUrl, {
      auth: {
        userId,
        appVersion,
        referrer: appName,
      },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('session:changed', () => onSessionChangedRef.current?.());

    // Deduplicate events (server emits to both Order# and Room# rooms)
    const recentEvents = new Set<string>();

    // Helper to handle order events
    const handleOrderEvent = (
      payload: OrderSocketEvent,
      toastType: 'info' | 'success' | 'warning' | 'error' = 'info'
    ) => {
      // Deduplicate: same orderId + eventType within a short window
      const eventKey = `${payload.orderId}:${payload.eventType}`;
      if (recentEvents.has(eventKey)) return;
      recentEvents.add(eventKey);
      setTimeout(() => recentEvents.delete(eventKey), 2000);

      // Invalidate all order-related queries
      queryClient.invalidateQueries({
        queryKey: ['order', String(payload.orderId)],
      });
      queryClient.invalidateQueries({
        queryKey: ['order', payload.orderId],
      });
      queryClient.invalidateQueries({ queryKey: ['myOrders'] });
      queryClient.invalidateQueries({ queryKey: ['buyer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['seller-orders'] });
      queryClient.invalidateQueries({ queryKey: ['ordersSummary'] });

      // Show toast only for events initiated by others
      if (payload.userId !== userId) {
        const eventConfig = EVENT_MESSAGES[payload.eventType];
        if (eventConfig) {
          notify[toastType](eventConfig.title, {
            description: eventConfig.description(payload.userName),
          });
        }
      }
    };

    // Order event handlers
    newSocket.on('order:created', (payload) =>
      handleOrderEvent(payload, 'info')
    );
    newSocket.on('order:confirmed', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    newSocket.on('order:rejected', (payload) =>
      handleOrderEvent(payload, 'error')
    );
    newSocket.on('order:paid', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    newSocket.on('order:shipped', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    newSocket.on('order:delivered', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    newSocket.on('order:cancelled', (payload) =>
      handleOrderEvent(payload, 'warning')
    );
    newSocket.on('order:message', (payload) =>
      handleOrderEvent(payload, 'info')
    );
    newSocket.on('order:review', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    newSocket.on('order:buyer-review', (payload) =>
      handleOrderEvent(payload, 'success')
    );
    // El vendedor editó un pedido manual, o un admin le movió el estado y las
    // fechas. El API lo emitía desde `updateManualOrder` y nadie lo escuchaba,
    // así que la pantalla del otro se quedaba con el pedido viejo.
    newSocket.on('order:updated', (payload) =>
      handleOrderEvent(payload, 'info')
    );

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [userId, wsUrl, appName, queryClient]);

  const joinOrder = useCallback(
    (orderId: number) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('order:join', orderId);
      }
    },
    [isConnected]
  );

  const leaveOrder = useCallback(
    (orderId: number) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('order:leave', orderId);
      }
    },
    [isConnected]
  );

  const contextValue = useMemo(
    () => ({
      isConnected,
      joinOrder,
      leaveOrder,
    }),
    [isConnected, joinOrder, leaveOrder]
  );

  return (
    <OrderSocketContext.Provider value={contextValue}>
      {children}
    </OrderSocketContext.Provider>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

export function useOrderSocket() {
  const context = useContext(OrderSocketContext);
  if (context === undefined) {
    throw new Error(
      'useOrderSocket must be used within an OrderSocketProvider'
    );
  }
  return context;
}

/**
 * Hook to automatically join/leave an order room when viewing an order
 */
export function useOrderRoom(orderId: number | undefined) {
  const { joinOrder, leaveOrder, isConnected } = useOrderSocket();

  useEffect(() => {
    if (!orderId || !isConnected) return;

    joinOrder(orderId);

    return () => {
      leaveOrder(orderId);
    };
  }, [orderId, isConnected, joinOrder, leaveOrder]);
}
